import React, { useCallback, useState, ElementType } from 'react';
import debounce from 'lodash/debounce';

import EmojiEntryComponent from './EmojiEntryComponent/EmojiEntryComponent';
import EmojiDataSetter from './EmojiDataSetter/EmojiDataSetter';

const getFormattedEmojis = ({ emojis, value }) =>
  emojis
    .filter(({ short_names }) => short_names.find(name => name.includes(value)))
    .map(emoji => ({
      ...emoji,
      name: emoji.native,
      colons: `:${emoji.short_names.find(name => name.includes(value))}`,
    }));

type Props = {
  emojiMentionPlugin: {
    MentionSuggestions: ElementType;
  };
  onOpen: Function;
  onClose: Function;
};

function EmojiSuggestions({ emojiMentionPlugin: { MentionSuggestions: Suggestions }, onOpen, onClose }: Props) {
  const [emojiList, setEmojiList] = useState([]);
  const [emojiIndex, setEmojiIndex] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const setAdditionalEmoji = useCallback(
    (value, emojiData) => setEmojiList(getFormattedEmojis({ emojis: emojiData.search(value), value })),
    []
  );

  const debouncedSetAdditionalEmoji = useCallback(debounce(setAdditionalEmoji, 1000), []);

  const handleSearch = useCallback(
    ({ value }) => {
      if (value.length === 1 && !isOpen) setIsOpen(true);
      if (value.length === 0) {
        setIsOpen(false);
        setEmojiIndex(null);
      }
      if (value.length <= 1 || !emojiIndex) {
        setEmojiList([]);
        debouncedSetAdditionalEmoji.cancel();
      } else {
        const emojis = getFormattedEmojis({ emojis: emojiIndex.search(value).splice(0, 10), value });

        setEmojiList(emojis);
        debouncedSetAdditionalEmoji(value, emojiIndex);
      }
    },
    [setAdditionalEmoji, isOpen, emojiIndex]
  );

  const handleAddMention = useCallback(() => {
    setEmojiList([]);
    setIsOpen(false);
    setEmojiIndex(null);
  }, []);

  return (
    <>
      {isOpen && <EmojiDataSetter setEmojiIndex={setEmojiIndex} />}
      <Suggestions
        suggestions={emojiList}
        onAddMention={handleAddMention}
        onSearchChange={handleSearch}
        entryComponent={EmojiEntryComponent}
        onOpen={onOpen}
        onClose={onClose}
      />
    </>
  );
}

export default EmojiSuggestions;
